import { useEffect, useRef, useState } from 'preact/hooks'
import { i18n } from 'i18n/i18n'

import {
  Calculation,
  ChosenItemTransfer,
  CurrencyExchangeDetails,
  TransferFeesObj,
} from 'models/exchange-transfer-calculate'
import { ExchangeDirection, SpinnerError } from 'models/calculator'

import { exchangeTransfer } from 'services/exchange-transfer-calculate'
import { Domains } from 'services/wp-api/domains-api'
import { HttpError, isHttpError, isHttpErrorStatus } from 'utils/promise'

import {
  DEFAULT_DEBOUNCE_TIME,
  DEFAULT_EXCHANGE_CALCULATION,
  DEFAULT_REFRESH_TIME,
  DEFAULT_TRANSFER_FEES,
  DEFAULT_TRANSFER_FROM_COUNTRY,
} from '../config'
import { useCountdownRefresh } from './useCountdownRefresh'

export type AmountName = 'baseAmount' | 'counterAmount'

interface TransferCalculationForm {
  baseAmount: string
  counterAmount: string
  baseCurrency: string
  counterCurrency: string
  direction: ExchangeDirection
  chosenCountryCode: string
  chosenItemTransfer: ChosenItemTransfer
}

export interface TransferCalculationHookProps extends TransferCalculationForm {
  domains: Domains
  setBaseAmount: (baseAmount: string) => void
  setCounterAmount: (counterAmount: string) => void
  setChosenItemTransfer: (chosenItemTransfer: ChosenItemTransfer) => void
  setSpinnerError: (spinnerError: SpinnerError) => void
}

const getApiErrorMessage = (error: HttpError): string => {
  const responseData = error.response?.data as { error?: string } | undefined
  return responseData?.error || ''
}

export const useTransferCalculation = ({
  baseAmount,
  counterAmount,
  baseCurrency,
  counterCurrency,
  direction,
  chosenCountryCode,
  chosenItemTransfer,
  domains,
  setBaseAmount,
  setCounterAmount,
  setChosenItemTransfer,
  setSpinnerError,
}: TransferCalculationHookProps) => {
  const [exchangeCalculation, setExchangeCalculation] = useState<Calculation>(DEFAULT_EXCHANGE_CALCULATION)
  const [transferFees, setTransferFees] = useState<TransferFeesObj[]>(DEFAULT_TRANSFER_FEES)
  const [apiErrorMessage, setApiErrorMessage] = useState<string>('')
  const [isCalculationLoading, setIsCalculationLoading] = useState(true)

  const formState: TransferCalculationForm = {
    baseAmount,
    counterAmount,
    baseCurrency,
    counterCurrency,
    direction,
    chosenCountryCode,
    chosenItemTransfer,
  }
  const formRef = useRef<TransferCalculationForm>(formState)
  formRef.current = formState

  const abortControllerRef = useRef<AbortController | undefined>(undefined)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const { countdown, stopCountdown, restartCountdown } = useCountdownRefresh({
    pollingIntervalSeconds: DEFAULT_REFRESH_TIME / 1000,
    onRefresh: () => calculateExchangeTransfer(),
  })

  const calculateExchangeTransfer = async (): Promise<void> => {
    stopCountdown()
    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    const {
      baseAmount,
      counterAmount,
      baseCurrency,
      counterCurrency,
      direction,
      chosenCountryCode: toCountry,
      chosenItemTransfer: itemTransfer,
    } = formRef.current
    const amountNameToUpdate: AmountName = direction === 'back' ? 'baseAmount' : 'counterAmount'
    const calculationItemToRead = direction === 'back' ? 'bought_amount' : 'sold_amount'
    const currencyExchangeDetailsTransfer: CurrencyExchangeDetails = {
      baseAmount: direction === 'forward' ? baseAmount : '',
      counterAmount: direction === 'forward' ? '' : counterAmount,
      baseCurrency,
      counterCurrency,
      direction,
    }

    try {
      const exchangeTransferCalculate = await exchangeTransfer(
        currencyExchangeDetailsTransfer,
        itemTransfer,
        DEFAULT_TRANSFER_FROM_COUNTRY,
        toCountry,
        abortController,
      )

      const { calculation, transferFees: transferFeesResponse } = exchangeTransferCalculate
      const isChosenItemTransferValid = transferFeesResponse.some(
        (transferFee) =>
          transferFee.transfer_type === itemTransfer.chosenItemTransferType &&
          transferFee.fee === itemTransfer.chosenItemTransferFee,
      )

      const nextAmount = `${calculation[calculationItemToRead]}`
      if (amountNameToUpdate === 'baseAmount') {
        setBaseAmount(nextAmount)
      } else {
        setCounterAmount(nextAmount)
      }
      setTransferFees(transferFeesResponse)
      setExchangeCalculation(calculation)
      if (!isChosenItemTransferValid) {
        const correctedItemTransfer: ChosenItemTransfer = {
          chosenItemTransferType: transferFeesResponse[0]?.transfer_type || '',
          chosenItemTransferFee: transferFeesResponse[0]?.fee || '',
        }
        // Write-back, not a user action: pre-sync prevFormRef so the form-change effect below
        // doesn't mistake it for a manual transfer type change and schedule a needless refetch.
        prevFormRef.current = { ...prevFormRef.current, chosenItemTransfer: correctedItemTransfer }
        setChosenItemTransfer(correctedItemTransfer)
      }
      setApiErrorMessage('')
      setSpinnerError({ isErrorVisible: false })
      setIsCalculationLoading(false)
      restartCountdown()
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        return
      }

      setIsCalculationLoading(false)

      if (isHttpErrorStatus(error, 429)) {
        setSpinnerError({
          isErrorVisible: true,
          message: i18n('calculator.spinnerError.toManyRequests'),
        })
        return
      }

      if (!isHttpError(error)) {
        setSpinnerError({ isErrorVisible: true })
        return
      }

      setSpinnerError({ isErrorVisible: false })
      setApiErrorMessage(getApiErrorMessage(error))
    }
  }

  useEffect(() => {
    if (domains?.base_user) {
      calculateExchangeTransfer()
    }
  }, [domains])

  const prevFormRef = useRef<TransferCalculationForm>(formState)

  useEffect(() => {
    const prevForm = prevFormRef.current
    prevFormRef.current = formState

    const currencyChanged = prevForm.baseCurrency !== baseCurrency || prevForm.counterCurrency !== counterCurrency
    if (currencyChanged) {
      // A currency switch recalculates immediately (no debounce), but only when both
      // amounts are filled; otherwise it only clears the current error status.
      if (Number(baseAmount) > 0 && Number(counterAmount) > 0) {
        calculateExchangeTransfer()
      } else {
        setApiErrorMessage('')
      }
      return
    }

    const activeAmountName: AmountName = direction === 'forward' ? 'baseAmount' : 'counterAmount'
    const activeAmount = activeAmountName === 'baseAmount' ? baseAmount : counterAmount
    const prevActiveAmount = activeAmountName === 'baseAmount' ? prevForm.baseAmount : prevForm.counterAmount
    const activeAmountChanged = prevActiveAmount !== activeAmount && !!+activeAmount

    const transferTypeChanged =
      prevForm.chosenItemTransfer.chosenItemTransferType !== chosenItemTransfer.chosenItemTransferType
    const countryChanged = prevForm.chosenCountryCode !== chosenCountryCode
    const bothAmountsFilled = !!+baseAmount && !!+counterAmount

    const isFormUpdate =
      activeAmountChanged || (transferTypeChanged && bothAmountsFilled) || (countryChanged && bothAmountsFilled)

    if (!isFormUpdate) {
      return
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => calculateExchangeTransfer(), DEFAULT_DEBOUNCE_TIME)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [baseAmount, counterAmount, baseCurrency, counterCurrency, direction, chosenCountryCode, chosenItemTransfer])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      abortControllerRef.current?.abort()
    }
  }, [])

  return { exchangeCalculation, transferFees, apiErrorMessage, countdown, isCalculationLoading }
}
